/**
 * View Parameters Type Definitions
 *
 * Type-safe parameter definitions for different view types in the deep linking system.
 * Each view type has its own parameter interface with validation functions.
 */

// BetSlip Parameters for deep link
export interface BetSlipParams {
  conid?: string; // Single contract (string from URL)
  productConid?: string; // Election product (string from URL)
}

// By-state view has no deep link params — ElectionHub reads it directly from route.query
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ByStateParams {}

// Union type for all view parameters
export type ViewParamsMap = {
  sharecontracts: BetSlipParams;
  sharedialog: BetSlipParams;
  'by-state': ByStateParams;
};

// Type for view handler functions
export type ViewHandler<T extends keyof ViewParamsMap> = (params: ViewParamsMap[T]) => Promise<void> | void;

// Generic type for any view handler
export type AnyViewHandler = ViewHandler<keyof ViewParamsMap>;

// Core validation helper - checks if string represents a valid positive integer
export function isValidPositiveInteger(value: string | undefined): boolean {
  if (!value) return true; // Optional parameter
  const num = Number(value);
  return !isNaN(num) && num > 0 && Number.isInteger(num);
}

// Semantic aliases for specific use cases (all use the same validation logic)
export const isValidBetSlipConid = isValidPositiveInteger;

// View type validation
export function isValidViewType(viewType: string): viewType is keyof ViewParamsMap {
  return ['sharecontracts', 'sharedialog', 'by-state'].includes(viewType);
}

// BetSlip parameter validation
export function validateBetSlipParams(params: Record<string, string>): {
  isValid: boolean;
  validatedParams: BetSlipParams;
  errors: string[];
} {
  const errors: string[] = [];
  const validatedParams: BetSlipParams = {};

  // Validate conid
  if (params.conid !== undefined) {
    if (isValidBetSlipConid(params.conid)) {
      validatedParams.conid = params.conid;
    } else {
      errors.push(`Invalid conid: ${params.conid}`);
    }
  }

  // Validate productConid
  if (params.productConid !== undefined) {
    if (isValidBetSlipConid(params.productConid)) {
      validatedParams.productConid = params.productConid;
    } else {
      errors.push(`Invalid productConid: ${params.productConid}`);
    }
  }

  // Require at least one param
  if (!validatedParams.conid && !validatedParams.productConid) {
    errors.push('Either conid or productConid is required');
  }

  return {
    isValid: errors.length === 0,
    validatedParams,
    errors,
  };
}

// Unified validation function
export function validateViewParams<T extends keyof ViewParamsMap>(
  viewType: T,
  params: Record<string, string>,
): {
  isValid: boolean;
  validatedParams: ViewParamsMap[T];
  errors: string[];
} {
  switch (viewType) {
    case 'sharecontracts':
    case 'sharedialog':
      return validateBetSlipParams(params) as {
        isValid: boolean;
        validatedParams: ViewParamsMap[T];
        errors: string[];
      };
    case 'by-state':
      return {
        isValid: true,
        validatedParams: {} as ViewParamsMap[T],
        errors: [],
      };
    default:
      return {
        isValid: false,
        validatedParams: {} as ViewParamsMap[T],
        errors: [`Unknown view type: ${viewType}`],
      };
  }
}
